<template>
  <div>
    <div v-if="loading" class="space-y-4 p-4">
      <div v-for="n in 3" :key="'sk-' + n" class="border border-gray-200 rounded-lg overflow-hidden">
        <div class="px-4 py-3 bg-gray-50 border-b border-gray-200">
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-2">
              <div class="h-3 bg-gray-200 rounded animate-pulse" :style="{ width: [120, 100, 140][n - 1] + 'px' }"></div>
              <div class="h-3 w-14 bg-gray-100 rounded animate-pulse"></div>
            </div>
            <div class="h-4 w-14 bg-gray-200 rounded animate-pulse"></div>
          </div>
          <div class="h-2.5 w-32 bg-gray-100 rounded animate-pulse mt-1.5"></div>
        </div>
        <div class="px-4 py-3 space-y-3">
          <div v-for="e in 2" :key="'sk-ev-' + e">
            <div class="h-2.5 w-20 bg-gray-200 rounded animate-pulse mb-1"></div>
            <div class="ml-3 space-y-1">
              <div class="h-2.5 bg-gray-100 rounded animate-pulse" :style="{ width: e === 1 ? '70%' : '55%' }"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-else-if="suspects.length === 0" class="text-center py-8 text-gray-500 text-xs">
      No suspect conditions found. Run rules from the Suspect Rules tab first.
    </div>
    <div v-else class="space-y-4 p-4">
      <div
        v-for="(suspect, idx) in suspects"
        :key="idx"
        class="border border-gray-200 rounded-lg overflow-hidden"
      >
        <!-- Rule Header -->
        <div class="px-4 py-3 bg-gray-50 border-b border-gray-200">
          <div class="flex items-center justify-between">
            <div>
              <span class="font-medium text-gray-900 text-xs">{{ suspect.rule_name }}</span>
              <span v-if="suspect.suspect_hcc" class="ml-2 text-[10px] text-gray-500">
                HCC {{ suspect.suspect_hcc }}
              </span>
            </div>
            <span :class="confidenceBadge(suspect.confidence_tier)" class="px-2 py-0.5 rounded text-[10px] font-medium">
              {{ suspect.confidence_tier }}
            </span>
          </div>
          <div v-if="suspect.suggested_icd_codes?.length" class="mt-1 text-[10px] text-gray-600">
            Suggest: <span class="font-mono">{{ suspect.suggested_icd_codes.map(c => c.code).join(', ') }}</span>
          </div>
        </div>

        <!-- Supporting Evidence -->
        <div class="px-4 py-3">
          <div
            v-for="(ev, evIdx) in suspect.supporting_evidence"
            :key="evIdx"
            class="mb-3 last:mb-0"
          >
            <div class="text-[10px] font-medium text-gray-700 mb-1">
              {{ ev.attribute }}
              <span v-if="ev.value" class="text-gray-500 font-normal">= {{ ev.value }}</span>
            </div>
            <div v-if="ev.records && ev.records.length > 0" class="ml-3 space-y-1">
              <div
                v-for="(rec, recIdx) in ev.records.slice(0, 5)"
                :key="recIdx"
                class="text-[10px] text-gray-600 flex items-center gap-2"
              >
                <span class="text-gray-400">&#x2022;</span>
                <span v-if="rec.source_type" class="text-gray-400">{{ rec.source_type }}:</span>
                <span v-if="rec.service_date">{{ formatDate(rec.service_date) }}</span>
                <span v-if="rec.manufacturer" class="font-medium">{{ rec.manufacturer }}</span>
                <span v-if="rec.ndc_code" class="font-mono text-gray-500">NDC {{ rec.ndc_code }}</span>
                <span v-if="rec.lab_value" class="font-medium">{{ rec.lab_value }}</span>
                <span v-if="rec.test_name" class="text-gray-500">({{ rec.test_name }})</span>
                <span v-if="rec.bmi_value" class="font-medium">{{ rec.bmi_value }} kg/m2</span>
              </div>
              <div v-if="ev.records.length > 5" class="text-[10px] text-gray-400 ml-3">
                + {{ ev.records.length - 5 }} more records
              </div>
            </div>
            <div v-else class="ml-3 text-[10px] text-gray-400 italic">
              No billing history (absence is the evidence)
            </div>
          </div>

          <div class="mt-3 pt-2 border-t border-gray-100 text-[10px] text-gray-500">
            <span class="font-medium">Evidence:</span> {{ suspect.evidence_citation }}
            <span v-if="suspect.evidence_source" class="text-gray-400">({{ suspect.evidence_source }})</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import abortable from '@/mixins/abortable';

export default {
  name: 'Patient_Suspects',
  mixins: [abortable],
  props: {
    patientIdentifier: { type: String, required: true },
    entityId: { type: Number, default: null },
  },
  data() {
    return {
      suspects: [],
      loading: false,
    };
  },
  watch: {
    patientIdentifier: {
      immediate: true,
      handler() {
        const signal = this._renewAbortSignal();
        this.loadData(signal);
      },
    },
  },
  methods: {
    async loadData(signal) {
      if (!this.patientIdentifier) return;
      this.loading = true;
      try {
        const response = await axios.get(
          `/api/coding/${this.patientIdentifier}/suspect-evidence/`,
          { signal },
        );
        this.suspects = response.data.suspects || [];
      } catch (error) {
        if (this._isAborted(error)) return;
        console.error('Failed to load suspect evidence:', error);
      } finally {
        if (!signal?.aborted) this.loading = false;
      }
    },
    formatDate(dateStr) {
      if (!dateStr) return '';
      const date = new Date(dateStr + 'T00:00:00');
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    },
    confidenceBadge(tier) {
      const base = 'inline-flex';
      switch (tier) {
        case 'very_high': return `${base} bg-green-100 text-green-800`;
        case 'high': return `${base} bg-blue-100 text-blue-800`;
        case 'medium': return `${base} bg-yellow-100 text-yellow-800`;
        case 'low': return `${base} bg-gray-100 text-gray-600`;
        default: return `${base} bg-gray-100 text-gray-600`;
      }
    },
  },
};
</script>
